import React, { useEffect, useState } from 'react';
import { ModelCard, ModelRowProps } from '../../component/Model';
import CardGallery from '../../component/CardGallery';
import { useSearchParams } from 'react-router-dom';
import { FilterOption } from '../../component/FilterBar';
import FilterBar from '../../component/FilterBar';
import Loading from '../../component/Loading';
import { Button, Modal, Typography, Space, Alert, message,Tabs, Tag, Table, Badge, Empty, Pagination } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { DeleteOutlined, SelectOutlined, CloseOutlined } from '@ant-design/icons';

interface FilterOptions {
  // Adjust these fields based on your model filter needs
  modelNames: string[];
  versions: string[];
  createdBy: string[];
  tags: string[];
  types: string[];
}

// Define a cache duration (e.g., 10 minutes)
const CACHE_DURATION_MS = 10 * 60 * 1000;

interface TrainingJobRowProps {
  id: string;
  run_id: string;
  status: 'queued'|'running'|'failed'|'completed'|'stopped';
  model_name: string;
  dataset_id?: number;
  dataset_name?: string;
  submitted_by?: string;
  started_at?: string;
  ended_at?: string | null;
  duration_seconds?: number;
  current_step?: string;
  artifact_uri?: string;
  mlflow_url?: string;
}

interface FilterCache {
  timestamp: number;
  data: FilterOptions;
}

interface SelectedItem {
  id: string;
  system_name: string;
}

type TabKey = 'models' | 'jobs';

const ModelManagementPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('limit') || '20', 10));
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filterLoading, setFilterLoading] = useState(true);

  const initialTab = (searchParams.get('tab') as TabKey) || 'models';
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const [jobsPage, setJobsPage] = useState<number>(parseInt(searchParams.get('jobs_page') || '1', 10));
  const [jobsPageSize, setJobsPageSize] = useState<number>(parseInt(searchParams.get('jobs_limit') || '20', 10));
  const [jobsTotalPages, setJobsTotalPages] = useState<number>(1);
  const [jobsTotalItems, setJobsTotalItems] = useState<number>(0);
  const [jobsLoading, setJobsLoading] = useState<boolean>(false);
  // jobs filters/sort
  const [jobsSortBy, setJobsSortBy] = useState<string>(searchParams.get('jobs_sort_by') || 'started_at');
  const [jobsSortOrder, setJobsSortOrder] = useState<'asc'|'desc'>((searchParams.get('jobs_sort_order') as any) || 'desc');
  const [jobsFilters, setJobsFilters] = useState({
    status: searchParams.getAll('jobs_status'),
    modelName: searchParams.getAll('jobs_modelName'),
    datasetId: searchParams.getAll('jobs_datasetId'),
    createdBy: searchParams.getAll('jobs_createdBy'),
    time_range: searchParams.getAll('jobs_time_range'),
  });

  const [jobs, setJobs] = useState<TrainingJobRowProps[]>([]);

  // Selection mode states
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [sortBy, setSortBy] = useState<string>(searchParams.get('sort_by') || 'timestamp');
  const [sortOrder, setSortOrder] = useState<'asc'|'desc'>((searchParams.get('sort_order') as any) || 'desc');
  
  // State for filter options from the backend
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    modelNames: [],
    versions: [],
    createdBy: [],
    tags: [],
    types: []
  });

  // Initialize filters from URL params
  const [filters, setFilters] = useState({
    modelName: getArrayParamFromUrl('modelName'),
    version: getArrayParamFromUrl('version'),
    createdBy: getArrayParamFromUrl('createdBy'),
    tag: getArrayParamFromUrl('tag'),
    type: getArrayParamFromUrl('type'),
    time_range: getArrayParamFromUrl('time_range'),
  });
  
  const [models, setModels] = useState<ModelRowProps[]>([]);

  // Helper function to get array parameters from URL
  function getArrayParamFromUrl(paramName: string): string[] {
    const params = searchParams.getAll(paramName);
    return params.length > 0 ? params : [];
  }

  const jobStatusBadge = (status: TrainingJobRowProps['status']) => {
    const map: Record<string, 'default'|'processing'|'error'|'success'|'warning'> = {
      queued: 'default',
      running: 'processing',
      failed: 'error',
      completed: 'success',
      stopped: 'warning'
    };
    return <Badge status={map[status]} text={status.toUpperCase()} />;
  };

  const jobsColumns: ColumnsType<TrainingJobRowProps> = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s) => jobStatusBadge(s),
    },
    { title: 'Model', dataIndex: 'model_name', key: 'model_name' },
    { title: 'Dataset', dataIndex: 'dataset_name', key: 'dataset_name' },
    { title: 'Submitted By', dataIndex: 'submitted_by', key: 'submitted_by' },
    {
      title: 'Started',
      dataIndex: 'started_at',
      key: 'started_at',
      render: (v) => v ? new Date(v).toLocaleString() : '-',
      sorter: true,
    },
    {
      title: 'Duration',
      key: 'duration',
      render: (_, r) => r.duration_seconds ? `${Math.floor(r.duration_seconds/60)}m ${r.duration_seconds%60}s` : '-',
      sorter: false,
    },
    { title: 'Step', dataIndex: 'current_step', key: 'current_step' },
    // {
    //   title: 'Actions',
    //   key: 'actions',
    //   render: (_, r) => (
    //     <Space>
    //       {r.mlflow_url && (
    //         <a href={r.mlflow_url} target="_blank" rel="noreferrer">Open in MLflow</a>
    //       )}
    //       {/* optionally add Cancel/Retry hooks here */}
    //     </Space>
    //   )
    // }
  ];

  // keep your updateUrlWithFilters, add a variant for jobs + tab
  const updateUrlWithTabAndParams = (tab: TabKey, params: Record<string, string | string[]>) => {
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set('tab', tab);
    Object.entries(params).forEach(([k, v]) => {
      // wipe previous values of this key to avoid duplicates
      newParams.delete(k);
      if (Array.isArray(v)) {
        v.forEach(val => newParams.append(k, val));
      } else if (v) {
        newParams.set(k, v);
      }
    });
    setSearchParams(newParams);
  };

  // When tab changes, keep URL in sync
  const onTabChange = (key: string) => {
    const k = key as TabKey;
    setActiveTab(k);
    updateUrlWithTabAndParams(k, {}); // just set tab
  };

  // Update URL with current filters
  const updateUrlWithFilters = (page: number, limit: number, filterValues: Record<string, any>) => {
    const newParams = new URLSearchParams();
    
    // Add pagination params
    newParams.set('page', page.toString());
    newParams.set('limit', limit.toString());
    
    // Add filter params
    Object.entries(filterValues).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // Clear previous values before adding new ones
        if (value.length > 0) {
          value.forEach(val => newParams.append(key, val));
        }
      } else if (value) {
        newParams.set(key, value);
      }
    });
    
    // Update the URL without reloading the page
    setSearchParams(newParams);
  };

  useEffect(() => {
    fetchModelsList(currentPage, pageSize, filters);
    fetchJobs(currentPage, pageSize, filters);
    // Try to load filters from cache first
    const cachedFilters = getFilterOptionsFromCache();
    if (cachedFilters) {
      setFilterOptions(cachedFilters);
      setFilterLoading(false);
    } else {
      fetchFilterOptions();
    }
  }, []);
  
  useEffect(() => {
    if (currentPage !== 1) {
      fetchJobs(currentPage, pageSize, filters);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchModelsList(currentPage, pageSize, filters);
  }, [currentPage, pageSize, sortBy, sortOrder, JSON.stringify(filters)]);

  useEffect(() => {
  const firstLoad = searchParams.get('first_load_done') !== '1';
  if (firstLoad && models.length === 0 && jobs.length > 0 && activeTab === 'models') {
    setActiveTab('jobs');
    updateUrlWithTabAndParams('jobs', { first_load_done: '1' });
  } else if (firstLoad) {
    // mark done
    updateUrlWithTabAndParams(activeTab, { first_load_done: '1' });
  }
}, [models, jobs]);

  const getFilterOptionsFromCache = (): FilterOptions | null => {
    try {
      const cachedData = localStorage.getItem('modelFilterOptionsCache');
      if (cachedData) {
        const cache: FilterCache = JSON.parse(cachedData);
        const now = new Date().getTime();
        if (now - cache.timestamp < CACHE_DURATION_MS) {
          console.log('Using cached model filter options');
          return cache.data;
        } else {
          console.log('Cache expired, fetching new data');
        }
      }
    } catch (error) {
      console.error('Error reading from cache:', error);
    }
    return null;
  };

  const saveFilterOptionsToCache = (data: FilterOptions) => {
    try {
      const cacheData: FilterCache = {
        timestamp: new Date().getTime(),
        data: data
      };
      localStorage.setItem('modelFilterOptionsCache', JSON.stringify(cacheData));
      console.log('Model filter options saved to cache');
    } catch (error) {
      console.error('Error saving to cache:', error);
    }
  };

  const fetchFilterOptions = async () => {
    setFilterLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/model-filter-options`, {
        method: 'GET',
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch model filter options');
      }
      
      const data = await response.json();
      setFilterOptions(data);
      saveFilterOptionsToCache(data);
    } catch (error) {
      console.error('Error fetching model filter options:', error);
    } finally {
      setFilterLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string | string[]) => {
    setFilters(prevFilters => ({ ...prevFilters, [key]: value }));
  };

  const handleApplyFilters = () => {
    setCurrentPage(1); // Reset to first page when applying new filters
    fetchJobs(1, pageSize, filters);
  };

  async function fetchModelsList(page: number, size: number, filterValues: Record<string, any>) {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: String(page),
        limit: String(size),
        sort_by: sortBy,           // your models sort state
        sort_order: sortOrder,     // your models sort state
      });

      Object.entries(filterValues || {}).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => queryParams.append(key, v));
        } else if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });

      // keep URL in sync for the Models tab (optional)
      updateUrlWithTabAndParams('models', {
        page: String(page),
        limit: String(size),
        sort_by: sortBy,
        sort_order: sortOrder,
        ...(Object.fromEntries(
          Object.entries(filterValues || {}).flatMap(([k, v]) => {
            const arr = Array.isArray(v) ? v : [String(v)];
            return [[k, arr]];
          })
        ))
      });

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/models?${queryParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch models');

      const data = await res.json();
      setModels(data.items);
      setTotalPages(data.total_pages);
      setTotalItems(data.total_count);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function fetchJobs(page: number, size: number, filterValues: Record<string, any>) {
    setJobsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: size.toString(),
        sort_by: jobsSortBy,
        sort_order: jobsSortOrder,
      });
      Object.entries(filterValues).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(val => queryParams.append(key, val));
        } else if (value) {
          queryParams.append(key, value);
        }
      });

      // keep URL in sync for jobs params
      updateUrlWithTabAndParams('jobs', {
        jobs_page: page.toString(),
        jobs_limit: size.toString(),
        jobs_sort_by: jobsSortBy,
        jobs_sort_order: jobsSortOrder,
        ...(Object.fromEntries(
              Object.entries(filterValues).flatMap(([k, v]) => {
                const arr = Array.isArray(v) ? v : [String(v)];
                return [[`jobs_${k}`, arr]];
              })
            )
)
      });

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/training-jobs?${queryParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch training jobs');
      const data = await res.json();
      setJobs(data.items);
      setJobsTotalPages(data.total_pages);
      setJobsTotalItems(data.total_count);
    } catch (e) {
      console.error(e);
    } finally {
      setJobsLoading(false);
    }
  }

  // Selection mode handlers
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedItems([]); // Clear selections when toggling mode
  };

  const handleItemSelect = (id: string, system_name: string) => {
    setSelectedItems(prev => {
      const exists = prev.find(item => item.id === id && item.system_name === system_name);
      if (exists) {
        // Remove from selection
        return prev.filter(item => !(item.id === id && item.system_name === system_name));
      } else {
        // Add to selection
        return [...prev, { id, system_name }];
      }
    });
  };

  const selectAllItems = () => {
    if (selectedItems.length === models.length) {
      // Deselect all
      setSelectedItems([]);
    } else {
      // Select all current page items
      const allItems = models.map(model => ({
        id: model.id.toString(),
        system_name: model.system_name || ''
      }));
      setSelectedItems(allItems);
    }
  };

  const handleDeleteConfirm = () => {
    setDeleteModalVisible(true);
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
  };

  const handleDeleteExecute = async () => {
    setDeleteLoading(true);
    try {
      // Prepare the model IDs for deletion
      const modelIds = selectedItems.map(item => item.id);
      console.log('Deleting models with IDs:', modelIds);
      console.log('Selected items:', selectedItems);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/models`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ model_names: modelIds })
      });

      if (!response.ok) {
        throw new Error('Failed to delete models');
      }

      const result = await response.json();
      
      // Show success message
      message.success(`Successfully deleted ${selectedItems.length} model(s)`);
      
      // Clear selections and exit selection mode
      setSelectedItems([]);
      setSelectionMode(false);
      
      // Refresh the models list
      await fetchJobs(currentPage, pageSize, filters);
      
    } catch (error) {
      console.error('Error deleting models:', error);
      message.error('Failed to delete models. Please try again.');
    } finally {
      setDeleteLoading(false);
      setDeleteModalVisible(false);
    }
  };

  // Get selected model names for confirmation modal
  const getSelectedModelNames = () => {
    return selectedItems.map(selected => {
      const model = models.find(m => m.id.toString() === selected.id);
      return model?.model_name || `Model ${selected.id}`;
    });
  };

  const filterBarOptions: FilterOption[] = [
    {
      name: "modelName",
      label: "Model Name",
      type: "select",
      options: filterOptions.modelNames,
    },
    {
      name: "version",
      label: "Version",
      type: "select",
      options: filterOptions.versions,
    },
    {
      name: "createdBy",
      label: "Created By",
      type: "select",
      options: filterOptions.createdBy,
    },
    {
      name: "tag",
      label: "Tags",
      type: "select",
      options: filterOptions.tags,
    },
    {
      name: "type",
      label: "Type",
      type: "select",
      options: filterOptions.types,
    },
  ];

  return (
  <div className="flex h-screen">
    <main className="flex-1 bg-gray-500 p-6">
      {loading && <Loading message="Loading Models..." />}

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        items={[
          {
            key: 'models',
            label: 'Models',
            children: (
              <>
                {/* Action Bar */}
                <div className="mb-4 flex justify-between items-center">
                  <div className="flex items-center space-x-4">
                    <Button
                      type={selectionMode ? "default" : "primary"}
                      icon={selectionMode ? <CloseOutlined /> : <SelectOutlined />}
                      onClick={toggleSelectionMode}
                    >
                      {selectionMode ? 'Cancel Selection' : 'Select Models'}
                    </Button>

                    {selectionMode && (
                      <>
                        <Button
                          onClick={selectAllItems}
                          disabled={models.length === 0}
                        >
                          {selectedItems.length === models.length ? 'Deselect All' : 'Select All'}
                        </Button>

                        <span className="text-sm text-gray-200">
                          {selectedItems.length} of {models.length} selected
                        </span>
                      </>
                    )}
                  </div>

                  {selectionMode && selectedItems.length > 0 && (
                    <Button
                      type="primary"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleDeleteConfirm}
                      disabled={deleteLoading}
                    >
                      Delete Selected ({selectedItems.length})
                    </Button>
                  )}
                </div>

                <FilterBar
                  filters={filters}
                  filterOptions={[
                    { name: "modelName", label: "Model Name", type: "select", options: filterOptions.modelNames },
                    { name: "version",   label: "Version",    type: "select", options: filterOptions.versions },
                    { name: "createdBy", label: "Created By", type: "select", options: filterOptions.createdBy },
                    { name: "tag",       label: "Tags",       type: "select", options: filterOptions.tags },
                    { name: "type",      label: "Type",       type: "select", options: filterOptions.types },
                  ]}
                  onFilterChange={handleFilterChange}
                  onApplyFilters={handleApplyFilters}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSortChange={(newSortBy, newSortOrder) => {
                    setSortBy(newSortBy);
                    setSortOrder(newSortOrder);
                    fetchModelsList(currentPage, pageSize, filters);
                  }}
                  isLoading={filterLoading}
                />

                <CardGallery
                  CardComponent={ModelCard}
                  cardClickPath="/model-management"
                  data={models}
                  loading={loading}
                  selectionMode={selectionMode}
                  selectedItems={selectedItems}
                  onItemSelect={handleItemSelect}
                  onPageChange={(page: number, size?: number) => {
                    setCurrentPage(page);
                    if (size && size !== pageSize) {
                      setPageSize(size);
                      fetchModelsList(1, pageSize, filters);
                    } else {
                      fetchModelsList(page, pageSize, filters);
                    }
                  }}
                  itemsPerPage={pageSize}
                  totalItems={totalItems}
                  totalPages={totalPages}
                  currentPage={currentPage}
                />

                {/* Delete Confirmation Modal */}
                <Modal
                  title="Confirm Deletion"
                  open={deleteModalVisible}
                  onOk={handleDeleteExecute}
                  onCancel={handleDeleteCancel}
                  okText="Delete"
                  cancelText="Cancel"
                  okType="danger"
                  confirmLoading={deleteLoading}
                  width={600}
                >
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Alert
                      message="Warning"
                      description="This action cannot be undone. The selected models will be permanently deleted."
                      type="warning"
                      showIcon
                    />
                    <div>
                      <Typography.Text strong>
                        You are about to delete {selectedItems.length} model(s):
                      </Typography.Text>
                      <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                        {getSelectedModelNames().map((name, index) => (
                          <li key={index}>
                            <Typography.Text>{name}</Typography.Text>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Space>
                </Modal>
              </>
            ),
          },
          {
            key: 'jobs',
            label: 'Training Jobs',
            children: (
              <>
                <div className="mb-4 flex items-center gap-3">
                  {/* Simple refresh; add status/model/dataset filters as needed */}
                  <Button onClick={() => fetchJobs(1, jobsPageSize, jobsFilters)}>Refresh</Button>
                </div>

                <Table
                  rowKey="id"
                  loading={jobsLoading}
                  columns={jobsColumns}
                  dataSource={jobs}
                  pagination={false} // Disable the built-in table pagination
                  onChange={(pagination, filters, sorter: any) => {
                    if (sorter?.field) {
                      setJobsSortBy(sorter.field);
                      setJobsSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
                      fetchJobs(jobsPage, jobsPageSize, jobsFilters);
                    }
                  }}
                />

                {(!jobsLoading && jobs.length === 0) && (
                  <div className="mt-6">
                    <Empty description={<div className="text-white">No training jobs yet.</div>} />
                  </div>
                )}

                {/* Custom pagination matching your first example */}
                <div className="pagination fixed-pagination mt-4 flex justify-center">
                  <Pagination
                    total={jobsTotalItems}
                    current={jobsPage}
                    onChange={(page, pageSize) => {
                      setJobsPage(page);
                      if (pageSize !== jobsPageSize) setJobsPageSize(pageSize);
                      fetchJobs(page, pageSize, jobsFilters);
                    }}
                    onShowSizeChange={(current, size) => {
                      setJobsPage(current);
                      setJobsPageSize(size);
                      fetchJobs(current, size, jobsFilters);
                    }}
                    showSizeChanger={true}
                    pageSizeOptions={['10', '20', '50', '100']}
                    pageSize={jobsPageSize}
                    showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                    disabled={jobsLoading}
                  />
                </div>
              </>
            ),
          },
        ]}
      />
    </main>
  </div>
);

};

export default ModelManagementPage;